import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, X } from 'lucide-react';
import NewsForm from './NewsForm';
import { newsApi, categoryApi } from '../../utils/api';
import { CATEGORY_LABEL } from '../../constants';

function AdminNews() {
  const [news, setNews] = useState([]);
  const [categories, setCategories] = useState([]);
  const [catFilter, setCatFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [newsRes, catRes] = await Promise.all([
        newsApi.getAll({ limit: 100 }),
        categoryApi.getAll(),
      ]);
      setNews(newsRes.data || []);
      setCategories(catRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setShowForm(false);
    setEditingId(null);
    // Public pages (home, article, vividh...) lai turantai naya data dekhauna
    window.dispatchEvent(new CustomEvent('kalonews:news-updated'));
    await fetchAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('के तपाईं यो समाचार मेटाउन निश्चित हुनुहुन्छ?')) return;
    try {
      await newsApi.delete(id);
      toast.success('समाचार मेटियो।');
      await fetchAll();
    } catch (error) {
      console.error('Error deleting news:', error);
      toast.error('समाचार मेटाउन असफल भयो।');
    }
  };

  if (loading) {
    return <div className="text-center py-16 text-muted">लोड हुँदै...</div>;
  }

  const closeForm = () => { setShowForm(false); setEditingId(null); };

  // श्रेणी अनुसार filter
  const filteredNews = catFilter === 'all' ? news : news.filter((n) => n.category_slug === catFilter);

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
        <h2 className="font-display text-2xl">समाचार व्यवस्थापन</h2>
        <button
          className="inline-flex items-center gap-1.5 bg-crimson text-white font-semibold text-[13.5px] px-4 py-2.5 rounded-full hover:bg-crimson-dark"
          onClick={() => { setEditingId(null); setShowForm(true); }}
        >
          <Plus size={15} /> नयाँ थप्नुहोस्
        </button>
      </div>

      {/* श्रेणी अनुसार व्यवस्थापन — filter chips */}
      <div className="flex items-center gap-2 flex-wrap mb-5">
        <button
          onClick={() => setCatFilter('all')}
          className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-bold border transition-colors ${
            catFilter === 'all' ? 'bg-crimson text-white border-crimson' : 'bg-white text-ink-soft border-line hover:border-crimson'
          }`}
        >
          सबै ({news.length})
        </button>
        {categories.map((c) => {
          const count = news.filter((n) => n.category_slug === c.slug).length;
          return (
            <button
              key={c.id}
              onClick={() => setCatFilter(c.slug)}
              className={`px-3.5 py-1.5 rounded-full text-[12.5px] font-bold border transition-colors ${
                catFilter === c.slug ? 'bg-crimson text-white border-crimson' : 'bg-white text-ink-soft border-line hover:border-crimson'
              }`}
            >
              {CATEGORY_LABEL[c.slug] || (c.slug === 'entertainment' ? 'समाज' : c.name)} ({count})
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-2.5">
        {filteredNews.map((n) => (
          <div key={n.id} className="flex items-center gap-3 bg-white border border-line rounded-xl p-3 hover:shadow-sm">
            <img src={n.image || 'https://via.placeholder.com/80x80?text=K'} alt="" className="w-[52px] h-[52px] rounded-lg object-contain bg-paper-tint flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold truncate">{n.title}</p>
              <span className="text-xs text-muted">
                {CATEGORY_LABEL[n.category_slug] || n.category_name} • {n.author} • {n.views || 0} भ्यु
              </span>
            </div>
            <div className="flex gap-1 flex-shrink-0">
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-paper-soft" onClick={() => { setEditingId(n.id); setShowForm(true); }} aria-label="Edit">
                <Pencil size={16} />
              </button>
              <button className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-red-50 hover:text-crimson" onClick={() => handleDelete(n.id)} aria-label="Delete">
                <Trash2 size={16} />
              </button>
            </div>
          </div>
        ))}
        {filteredNews.length === 0 && <p className="text-muted py-4">यो श्रेणीमा कुनै समाचार छैन।</p>}
      </div>

      {/* नयाँ/सम्पादन फारम — pop up modal ko rup ma */}
      {showForm && (
        <div
          className="fixed inset-0 z-50 bg-black/60 overflow-y-auto p-4 sm:p-6"
          onClick={closeForm}
        >
          <div
            className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl my-4 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-3 px-5 sm:px-6 pt-5">
              <h3 className="font-display text-xl">
                {editingId ? 'समाचार सम्पादन गर्नुहोस्' : 'नयाँ समाचार थप्नुहोस्'}
              </h3>
              <button
                className="inline-flex items-center justify-center w-9 h-9 rounded-[10px] hover:bg-paper-soft flex-shrink-0"
                onClick={closeForm}
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-5 sm:p-6 pt-4">
              <NewsForm
                key={editingId || 'new'}
                newsId={editingId}
                categories={categories}
                onCancel={closeForm}
                onSave={handleSave}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminNews;